
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { EmptyProgramState } from "./EmptyProgramState";
import { DeleteProgramsDialog } from "./DeleteProgramsDialog";
import { useProgramsData } from "./hooks/useProgramsData";
import { ProgramsHeader } from "./ProgramsHeader";
import { ProgramTable } from "./ProgramTable";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";

export function ProgramsList() {
  const navigate = useNavigate();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { isBetaTier, isFounder, refreshPermissions } = usePermissionCheck();
  
  const {
    isLoading,
    searchQuery,
    setSearchQuery,
    selectedPrograms,
    filteredPrograms,
    toggleProgramSelection,
    toggleSelectAll,
    handleDeleteSelectedPrograms
  } = useProgramsData();

  useEffect(() => {
    // Refresh permissions when the page loads
    refreshPermissions();
  }, [refreshPermissions]);

  const handleProgramClick = (programId: string) => {
    navigate(`/programs/${programId}`);
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    const success = await handleDeleteSelectedPrograms();
    setIsDeleting(false);
    if (success) {
      setIsDeleteDialogOpen(false);
      // Refresh permissions after deletion in case it affects limits
      refreshPermissions();
    }
  };

  const handleCreateProgramClick = () => {
    // Navigate to create program page
    navigate('/create-program');
  };

  return (
    <>
      <ProgramsHeader 
        totalCount={filteredPrograms.length}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        selectedCount={selectedPrograms.length}
        showDeleteButton={filteredPrograms.length > 0}
        onDeleteClick={() => setIsDeleteDialogOpen(true)}
        isBetaTier={isBetaTier}
        isFounder={isFounder}
        onCreateProgramClick={handleCreateProgramClick} // Add this prop
      />

      <div className="glass rounded-xl animate-slide-in">
        {isLoading ? (
          <div className="p-8 text-center text-white/70">Loading...</div>
        ) : filteredPrograms.length === 0 ? (
          <EmptyProgramState />
        ) : (
          <ProgramTable 
            programs={filteredPrograms}
            selectedPrograms={selectedPrograms}
            toggleProgramSelection={toggleProgramSelection}
            toggleSelectAll={toggleSelectAll}
            onProgramClick={handleProgramClick}
          />
        )}
      </div>

      <DeleteProgramsDialog
        isOpen={isDeleteDialogOpen}
        isDeleting={isDeleting}
        selectedCount={selectedPrograms.length}
        onClose={() => setIsDeleteDialogOpen(false)}
        onDelete={handleDelete}
      />
    </>
  );
}
